/**
 * DismissControl — the one way Jess clears a question Merlin wants sent, a
 * sent ask, or a checklist item (kanban 59a1ee04; Scott / Jess 2026-10-21).
 *
 * Two paths, both his (Scott: "I'll give you the option to dismiss one and
 * add a note if you want to, or you can just dismiss it"):
 *   - plain: ONE click, nothing typed — only the audit row is written;
 *   - with a note: a line in his own words ("already answered in the orders
 *     review meeting", "Rich already asked the customer") — kept on the row,
 *     on the audit trail, and taught to Merlin so the next proposal round
 *     does not re-raise it.
 *
 * The same control on every surface (draft card, sent-ask card, the "Who
 * we've asked" rail, the case file's outstanding items, the Do next desk),
 * so the words and the recorder rows are the same everywhere. Each press is
 * recorded under the caller's names (qcp_ask_dismiss / qcp_ask_dismiss_note
 * for asks) with the ids and where it sits — never the note's words.
 *
 * The note <form> carries its own family name too (track.form, e.g.
 * qcp_draft_reason): the shared Guru recorder names an un-annotated form
 * by its accessible name, and ours is "say why: the draft to <person>", so
 * a contact's name landed in the action column (kanban 529c0851). The ids
 * in track.targets become data-target-* on the control, so every press
 * and the form submit carry the thread / request / case, never a name.
 */
import { useId, useState } from 'react';

import { trackEvent } from '../../services/portalTracking';
import styles from './DismissControl.module.css';

export interface DismissControlProps {
  /** What is being dismissed, for the accessible names ("the ask to Kevin Platt"). */
  what: string;
  /** Does the dismissing: resolves when it landed, rejects when it did not. */
  onDismiss: (note: string | null) => Promise<void>;
  /** Recorder names for the two paths, plus where the control sits. */
  track: {
    plain: string;
    note: string;
    /** The note form's submit row. Defaults to `${note}_form`. */
    form?: string;
    /** Ids for data-target-* on the control (snake_case keys), never a name. */
    targets?: Record<string, string | number | null | undefined>;
    context?: Record<string, string | number | boolean | string[] | null | undefined>;
  };
  /** Button words. */
  label?: string;
  noteLabel?: string;
  /** The note form's prompt. */
  placeholder?: string;
  disabled?: boolean;
  /** 'card' sits in a card's action row; 'row' is the compact rail/list form. */
  size?: 'card' | 'row';
  /** Red words (a sent ask or a checklist item), or quiet grey (a draft). */
  danger?: boolean;
  /** false: the caller prints its own refusal (the case file's row error);
   *  the press is still recorded as failed. */
  showError?: boolean;
}

/** data-target-<key> attributes for the wrap: thread_id -> data-target-thread-id. */
function targetAttrs(targets: DismissControlProps['track']['targets']): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(targets ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    out[`data-target-${key.replace(/_/g, '-')}`] = String(value);
  }
  return out;
}

export function DismissControl({
  what,
  onDismiss,
  track,
  label = 'Dismiss',
  noteLabel = 'Dismiss with a note',
  placeholder = 'Why? e.g. already answered in the orders review meeting',
  disabled = false,
  size = 'card',
  danger = false,
  showError = true,
}: DismissControlProps) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();

  const run = async (withNote: boolean) => {
    if (busy || disabled) return;
    const text = withNote ? note.trim() : '';
    if (withNote && !text) return;
    const name = withNote ? track.note : track.plain;
    setBusy(true);
    setError(null);
    try {
      await onDismiss(withNote ? text : null);
      trackEvent('action', name, { ...track.context, note_length: text.length, result: 'saved' });
      setOpen(false);
      setNote('');
    } catch {
      trackEvent('action', name, { ...track.context, note_length: text.length, result: 'failed' });
      if (showError) setError('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const btn = `${styles.btn} ${size === 'row' ? styles.btnRow : ''} ${danger ? styles.danger : ''}`;

  return (
    <div
      className={`${styles.wrap} ${size === 'row' ? styles.wrapRow : ''}`}
      data-testid="dismiss-control"
      {...targetAttrs(track.targets)}
    >
      {!open && (
        <div className={styles.buttons}>
          <button
            type="button"
            className={btn}
            disabled={busy || disabled}
            aria-label={`${label}: ${what}`}
            title="One click: it leaves your list; nothing is sent to anyone"
            onClick={() => void run(false)}
            data-action={track.plain}
          >
            {label}
          </button>
          <button
            type="button"
            className={`${styles.btn} ${size === 'row' ? styles.btnRow : ''} ${styles.quiet}`}
            disabled={busy || disabled}
            aria-label={`${noteLabel}: ${what}`}
            title="Say why, so Merlin learns from it and does not raise it again"
            onClick={() => setOpen(true)}
            data-action={`${track.note}_open`}
          >
            {noteLabel}
          </button>
        </div>
      )}
      {open && (
        <form
          className={styles.form}
          aria-label={`${noteLabel}: ${what}`}
          data-action={track.form ?? `${track.note}_form`}
          onSubmit={(e) => {
            e.preventDefault();
            void run(true);
          }}
        >
          <label className={styles.label} htmlFor={noteId}>
            Why? Merlin learns from it.
          </label>
          <textarea
            id={noteId}
            className={styles.textarea}
            rows={2}
            value={note}
            placeholder={placeholder}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <div className={styles.buttons}>
            <button
              type="submit"
              className={`${btn} ${styles.submit}`}
              disabled={busy || !note.trim()}
              data-action={track.note}
            >
              {label}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${size === 'row' ? styles.btnRow : ''} ${styles.quiet}`}
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Keep it
            </button>
          </div>
        </form>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default DismissControl;
